import React, { useEffect, useMemo, useState } from "react";
import { Tag, MagnifyingGlass } from "@phosphor-icons/react";
import { AvatarDisplay } from "@/components/Avatar";
import { StatusBadge } from "@/components/StatusBadge";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

export function AdminBuddies() {
  const { t } = useApp();
  const [data, setData] = useState({ total: 0, buddies: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [sort, setSort] = useState("newest");

  useEffect(() => { api.get("/admin/buddies").then((r) => setData(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    let list = data.buddies.filter((b) => {
      const s = q.trim().toLowerCase();
      const matchQ = !s || (b.label || "").toLowerCase().includes(s) || (b.code || "").toLowerCase().includes(s);
      const matchS = status === "ALL" || b.status === status;
      return matchQ && matchS;
    });
    list = [...list].sort((a, b) => (a.created_at < b.created_at ? 1 : -1) * (sort === "newest" ? 1 : -1));
    return list;
  }, [data, q, status, sort]);

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } };

  const Row = ({ b }) => (
    <div data-testid={`admin-buddy-${b.code}`} className="flex items-center gap-3 border-2 border-gray-800 rounded-2xl px-3 py-2 bg-white">
      <AvatarDisplay type={b.avatar_type} value={b.avatar_value} size={36} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-heading text-sm text-gray-900 truncate">{b.label || "—"} <span className="text-gray-400 font-body">/t/{b.code}</span></p>
        <p className="text-xs font-body text-gray-500">{b.scans} · {b.calls} · {b.messages} · {fmt(b.created_at)}</p>
      </div>
      <StatusBadge status={b.status} />
    </div>
  );

  const first = filtered.slice(0, 10);
  const rest = filtered.slice(10);

  return (
    <div className="sb-card p-5" data-testid="admin-buddies">
      <h2 className="font-heading font-semibold text-gray-900 mb-1 flex items-center gap-2"><Tag size={22} weight="duotone" style={{ color: "var(--app-strong)" }} />{t("adminBuddiesTitle")}</h2>
      <p className="text-sm font-body text-gray-500 mb-3">{t("adminTotalTags")}: <b>{data.total}</b></p>

      <div className="relative mb-2">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input data-testid="admin-buddy-search" className="sb-input pl-9" placeholder={t("searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="flex gap-2 mb-3">
        <select data-testid="admin-buddy-status" className="sb-input flex-1 !py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ALL">{t("filterAll")}</option>
          <option value="ACTIVE">{t("adminActiveTags")}</option>
          <option value="BLOCKED">{t("adminBlockedTags")}</option>
        </select>
        <select data-testid="admin-buddy-sort" className="sb-input flex-1 !py-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="newest">{t("sortNewest")}</option>
          <option value="oldest">{t("sortOldest")}</option>
        </select>
      </div>

      <div className="space-y-2" data-testid="admin-buddies-list">
        {filtered.length === 0 && <p className="font-body text-sm text-gray-500">—</p>}
        {first.map((b) => <Row key={b.id} b={b} />)}
      </div>
      {rest.length > 0 && (
        <details className="mt-2" data-testid="admin-buddies-more">
          <summary className="cursor-pointer font-heading text-sm font-medium py-2" style={{ color: "var(--app-strong)" }}>{t("showMore")} ({rest.length})</summary>
          <div className="space-y-2 mt-2">
            {rest.map((b) => <Row key={b.id} b={b} />)}
          </div>
        </details>
      )}
    </div>
  );
}
